import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useRef,
} from "react";
import { Form, message, Spin } from "antd";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import LocalStorageService from "../../../../utils/localStorageServices";
import { useQueryClient } from "@tanstack/react-query";
import CardSection from "../../../stripe/CardSection";
import CommonForm from "../../../global/form";
import SOURCES from "../../../../utils/constants";

const localStorageServices = LocalStorageService.getService();

const EnhancedUpdatePayment = forwardRef(({ userData, paymentPlans }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const currentUser = localStorageServices.getCurrentUser();
    const isTrialUser = currentUser?.user?.isTrial;

    const [loading, setLoading] = useState(false);
    const [subscriptionId, setSubscriptionId] = useState("");
    const [paymentStripePlanId, setPaymentStripePlanId] = useState("");
    const [paymentPlanPrice, setPaymentPlanPrice] = useState(0);
    const [paymentPlanCode, setPaymentPlanCode] = useState("");

    // Stripe state (ISOLATED)
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState(null);


    /* -------------------- Mutations -------------------- */

    const validateCouponMutation = useGenericMutation({
        endpoint: "/coupon/validate-coupon",
    });

    const resubscriptionMutation = useGenericMutation({
        endpoint: "/payment/resubscription"
    });

    const subscriptionMutation = useGenericMutation({
        endpoint: "/payment/subscription",
    });

    const updatePlanMutation = useGenericMutation({
        method: "PUT",
        endpoint: "user/plan",
    });

    /* -------------------- Form Fields -------------------- */

    const [paymentData, setPaymentData] = useState([
        {
            label: "Your Current Plan",
            name: "currentPlan",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            readOnly: true,
            span: 24,
        },
        {
            label: "Change Plan",
            name: "paymentPlanId",
            inputType: SOURCES.FORMINPUTTYPES.SELECT,
            required: true,
            message: "Please select a plan",
            options: [],
            span: 24,
        },
        {
            label: "Promo Code",
            name: "coupon",
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            span: 24,
            placeholder: "Enter coupon if any...",
        },
    ]);

    /* -------------------- Effects -------------------- */

    useEffect(() => {
        if (!paymentPlans || !userData) return;

        const user = userData;

        const currentPlanId =
            user?.PaymentPlanInfo?.[0]?._id;

        const currentPlan = paymentPlans.find(
            (p) => p._id === currentPlanId
        );

        const options = paymentPlans
            .filter((p) => p.isPrivatePlan === "n")
            .map((p) => ({
                label: p.planName,
                value: p._id,
            }));

        if (!currentPlan) return;

        // 1️⃣ Update state first
        setPaymentPlanPrice(currentPlan.planPrice);
        setPaymentPlanCode(currentPlan.planCode);
        setPaymentStripePlanId(currentPlan.stripePlanId);

        // 2️⃣ Update form schema
        setPaymentData((prev) =>
            prev.map((item) => {
                if (item.name === "paymentPlanId") {
                    return { ...item, options };
                }
                return item;
            })
        );

        // 3️⃣ WAIT for fields to mount, THEN set values
        form.setFieldsValue({
            currentPlan: currentPlan.planName,
            paymentPlanId: currentPlan._id,
        });
    }, [paymentPlans, userData, form]);



    /* -------------------- Handlers -------------------- */

    const handleValuesChange = (changedValues) => {
        if (!changedValues.paymentPlanId) return;

        const selectedPlan = paymentPlans.find(
            (p) => p._id === changedValues.paymentPlanId
        );


        if (!selectedPlan) return;

        setPaymentStripePlanId(selectedPlan.stripePlanId);
        setPaymentPlanPrice(selectedPlan.planPrice);
        setPaymentPlanCode(selectedPlan.planCode);
    };

    const handleCardChange = (event) => {
        setCardComplete(event.complete);
        setCardError(event.error?.message || null);
    };

    /* -------------------- Submit -------------------- */

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const { coupon, paymentPlanId: selectedPlanId } = values;

            if (coupon) {
                await validateCouponMutation.mutateAsync({ coupon });
            }


            if (paymentPlanCode === "p1") {
                await updatePlanMutation.mutateAsync({
                    paymentPlanId: selectedPlanId,
                    subscription_id: "",
                });
                message.success("Plan updated successfully");
                return;
            }

            if (!stripe || !elements) {
                throw new Error("Stripe not loaded");
            }

            if (!cardComplete) {
                setCardError("Please enter complete card details.");
                throw new Error("Fill Card Details");
            }
            const cardElement = elements.getElement(CardElement);

            const { error, paymentMethod } =
                await stripe.createPaymentMethod({
                    type: "card",
                    card: cardElement,
                    billing_details: {
                        email: currentUser.user.email,
                        name: currentUser.user.name,
                    },
                });

            if (error) throw new Error(error.message);

            const payload = {
                payment_method: paymentMethod.id,
                email: currentUser.user.email,
                name: currentUser.user.name,
                stripePlanId: paymentStripePlanId,
                coupon,
            };

            const res =
                isTrialUser === "n"
                    ? await resubscriptionMutation.mutateAsync(payload)
                    : await subscriptionMutation.mutateAsync(payload);

            const { client_secret, status, subscription_id } = res?.data;
            if (status === "requires_action") {
                await stripe.confirmCardPayment(client_secret);
            }

            await updatePlanMutation.mutateAsync({
                paymentPlanId: selectedPlanId,
                subscription_id,
            });

            message.success("Your payment has been received");
            queryClient.invalidateQueries({
                queryKey: ["user", "detail", "me"],
            });
        } catch (err) {
            message.error(err.message || "Payment failed");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    // if (isPlansLoading)
    //     return (
    //         <div className="p-10 text-center">
    //             <Spin />
    //         </div>
    //     );

    /* -------------------- RENDER -------------------- */

    return (
        <div className="w-full space-y-4">
            <CommonForm
                form={form}
                data={paymentData}
                onValuesChange={handleValuesChange}
                loading={loading}
            />

            {paymentPlanCode !== "p1" && (
                <div>
                    <label className="block text-sm font-medium mb-1">
                        Card Details
                    </label>
                    <CardSection onChange={handleCardChange} />
                    {cardError && (
                        <div className="text-red-500 text-xs mt-1">
                            {cardError}
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center gap-2 text-gray-700">
                <span className="text-xs uppercase text-gray-400">
                    Plan Price
                </span>
                <span className="text-lg font-bold text-primary">
                    {paymentPlanPrice} $
                </span>
            </div>
        </div>
    );
});

export default EnhancedUpdatePayment;
